import Repository from './repository';
import type {
	Channel,
	ControlLevel,
	DefinitionView,
	MerchantPreferenceItem,
	TemplateOwnerType,
	TemplateView,
	TriggerType,
	UserPreferenceItem,
} from '@arritech/shared/types';

const resource = '/notification';

/** The gateway's standard response envelope. */
interface ApiEnvelope<T> {
	success: boolean;
	data: T;
	errors?: Array<{ message: string }>;
}
type ApiResult<T> = Promise<{ data: ApiEnvelope<T> }>;

// ── Request payloads (derived from the domain view types) ─────────────────────

export type PutUserPreferencePayload = {
	notificationKey: string;
	channel: Channel;
	enabled: boolean;
};

export type PutMerchantPreferencePayload = {
	notificationKey: string;
	channel: Channel;
	allowUserOverride: boolean;
};

export interface ListDefinitionsParams {
	category?: string;
	controlLevel?: ControlLevel;
	channel?: Channel;
	triggerType?: TriggerType;
}

export interface PutTemplatePayload {
	notificationKey: string;
	channel: Channel;
	language?: string;
	subject?: string;
	title?: string;
	content: string;
}

/**
 * Drops the merchant's override for one (notification, channel, language). No
 * body content: what takes over is whatever the platform currently ships, and
 * sending the copy on screen would re-post the very version being reverted away
 * from.
 */
export interface RevertTemplatePayload {
	notificationKey: string;
	channel: Channel;
	language?: string;
}

export interface PreviewTemplatePayload {
	notificationKey: string;
	channel: Channel;
	/** BCP 47; the service falls back to "en" when omitted. */
	language?: string;
	/**
	 * An unsaved body to render instead of the stored template. Setting `content`
	 * bypasses resolution entirely, which is what lets an editor preview what is on
	 * screen without saving first.
	 */
	subject?: string;
	title?: string;
	content?: string;
	/**
	 * Optional overrides. Anything omitted is filled server-side from the
	 * definition's variable contract plus the system variables a delivery would
	 * resolve, so the usual call sends none. A value supplied here for a variable
	 * the contract marks sensitive must carry the literal "PREVIEW": the service
	 * rejects a live link or credential rather than rendering it.
	 */
	variables?: Record<string, unknown>;
}

export interface PreviewTemplateResult {
	channel: Channel;
	language: string;
	subject?: string;
	title?: string;
	content: string;
	resolvedTemplateVersion?: {
		templateFamilyKey: string;
		ownerType: TemplateOwnerType;
		version: number;
	};
}

export interface InboxParams {
	unreadOnly?: string;
	dismissed?: string;
	before?: string;
	limit?: number;
}

/**
 * Typed API client for the notification platform. Preference and inbox methods
 * use the shared domain types; the legacy `thanos_core`-era methods above them are
 * kept (loosely typed) while the dual-read transition is in flight. Endpoints not
 * yet exposed by the gateway (definitions/catalog, rules, template preview, audit)
 * are intentionally absent — they are added here once their routes land, rather
 * than shipped as client methods that 404.
 */
export default {
	// ── Legacy (thanos_core-era) notification endpoints ─────────────────────────
	find(payload: unknown) {
		return Repository.postWithAuth(`${resource}/find`, payload);
	},
	getByID(notificationId: string) {
		return Repository.getWithAuth(`${resource}/${notificationId}`);
	},
	read(payload: unknown) {
		return Repository.postWithAuth(`${resource}/read`, payload);
	},
	unread(payload: unknown) {
		return Repository.postWithAuth(`${resource}/unread`, payload);
	},
	count() {
		return Repository.getWithAuth(`${resource}/count`);
	},
	getDashboardData() {
		return Repository.getWithAuth(`${resource}/dashboard_data`);
	},
	countUnread() {
		return Repository.getWithAuth(`${resource}/count_unread`);
	},
	delete(payload: unknown) {
		return Repository.deleteWithAuth(`${resource}/delete`, payload);
	},
	deleteByReceiver(payload: unknown) {
		return Repository.postWithAuth(`${resource}/delete_by_receiver`, payload);
	},

	// ── Notification-platform catalog ───────────────────────────────────────────
	// The Platform tier is hidden server-side for non-Root callers; there is no
	// client-side scope parameter to get around it.
	listDefinitions(
		params?: ListDefinitionsParams
	): ApiResult<{ definitions: DefinitionView[] }> {
		return Repository.getWithAuth(`${resource}/definitions`, null, null, {
			params: params || {},
		});
	},

	// The stored template behind a notification, placeholders intact — what an
	// editor loads. previewTemplate renders those away, so the two are not
	// interchangeable.
	getTemplate(params: {
		notificationKey: string;
		channel: Channel;
		language?: string;
	}): ApiResult<TemplateView> {
		return Repository.getWithAuth(`${resource}/templates`, null, null, {
			params,
		});
	},

	// Saves the merchant's own version. The merchant is taken from the auth scope,
	// and the service refuses anything the catalog does not mark customizable.
	putTemplate(payload: PutTemplatePayload): ApiResult<TemplateView> {
		return Repository.putWithAuth(`${resource}/templates`, payload);
	},

	// Withdraws the merchant's override so the platform template applies again, and
	// answers with the platform template now in force — the editor repaints from the
	// response rather than guessing what it fell back to.
	revertTemplate(payload: RevertTemplatePayload): ApiResult<TemplateView> {
		return Repository.postWithAuth(`${resource}/templates/revert`, payload);
	},

	// Renders a template with sample data. The merchant whose override is resolved
	// comes from the auth scope, not from this payload.
	previewTemplate(
		payload: PreviewTemplatePayload
	): ApiResult<PreviewTemplateResult> {
		return Repository.postWithAuth(`${resource}/templates/preview`, payload);
	},

	// ── Notification-platform preferences ───────────────────────────────────────
	// user/merchant are resolved server-side from the auth scope (me) or the
	// merchantId query (merchant; PlatformOwner→own, Root→any).
	getMyPreferences(): ApiResult<{ preferences: UserPreferenceItem[] }> {
		return Repository.getWithAuth(`${resource}/preferences/me`);
	},
	putMyPreference(payload: PutUserPreferencePayload): ApiResult<unknown> {
		return Repository.putWithAuth(`${resource}/preferences/me`, payload);
	},
	getMerchantPreferences(
		merchantId: string
	): ApiResult<{ preferences: MerchantPreferenceItem[] }> {
		return Repository.getWithAuth(
			`${resource}/preferences/merchant`,
			null,
			null,
			{ params: { merchantId } }
		);
	},
	putMerchantPreference(
		payload: PutMerchantPreferencePayload,
		merchantId: string
	): ApiResult<unknown> {
		return Repository.putWithAuth(
			`${resource}/preferences/merchant?merchantId=${encodeURIComponent(
				merchantId
			)}`,
			payload
		);
	},

	// ── Notification-platform in-app inbox (InAppNotification) ───────────────────
	// Read side of the dual-read transition; user/merchant from the auth scope.
	inbox(params?: InboxParams) {
		return Repository.getWithAuth(`${resource}/inbox`, null, null, {
			params: params || {},
		});
	},
	inboxGetById(id: string) {
		return Repository.getWithAuth(`${resource}/inbox/item`, null, null, {
			params: { id },
		});
	},
	inboxUnreadCount() {
		return Repository.getWithAuth(`${resource}/inbox/unread-count`);
	},
	inboxRead(payload: unknown) {
		return Repository.putWithAuth(`${resource}/inbox/read`, payload);
	},
	inboxDismiss(payload: unknown) {
		return Repository.putWithAuth(`${resource}/inbox/dismiss`, payload);
	},
	inboxReadBatch(payload: unknown) {
		return Repository.putWithAuth(`${resource}/inbox/read-batch`, payload);
	},
	inboxUnreadBatch(payload: unknown) {
		return Repository.putWithAuth(`${resource}/inbox/unread-batch`, payload);
	},
	inboxDismissBatch(payload: unknown) {
		return Repository.putWithAuth(`${resource}/inbox/dismiss-batch`, payload);
	},
};
